import { useRef } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { RouteGuardPermissions } from "@/components/permissions/route-guard-multiple";
import CreditNotesForm from "@/components/invoices/credits/credit-notes-form";
import { api } from "@/convex/_generated/api";
import { Id } from "@/convex/_generated/dataModel";
import { Skeleton } from "@/components/ui/skeleton";

export const Route = createFileRoute(
  "/_authenticated/invoices/credit-notes/new",
)({
  validateSearch: (search: Record<string, unknown>) => ({
    draft: typeof search.draft === "string" ? search.draft : undefined,
  }),
  component: NewCreditNotePage,
});

function NewCreditNotePage() {
  const { draft } = Route.useSearch();

  // Capturamos el ?draft SOLO al montar. En creación nueva el form agrega el
  // param in-place (sin remontar → sin skeleton). En una recarga con ?draft
  // hidratamos ese borrador.
  const coldDraftRef = useRef(draft);

  return (
    <RouteGuardPermissions
      permissions={[{ module: "credit_notes", actions: ["create"] }]}
    >
      {coldDraftRef.current ? (
        <CreditNoteHydrator
          creditNoteId={coldDraftRef.current as Id<"credit_notes">}
        />
      ) : (
        <CreditNotesForm />
      )}
    </RouteGuardPermissions>
  );
}

function CreditNoteHydrator({
  creditNoteId,
}: {
  creditNoteId: Id<"credit_notes">;
}) {
  const creditNote = useQuery(
    api.companies.credit_notes.queries.getCreditNoteById,
    { creditNoteId },
  );

  if (creditNote === undefined) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!creditNote || creditNote.status !== "draft") {
    // Borrador inexistente o ya emitido → arrancar uno nuevo.
    return <CreditNotesForm />;
  }

  return <CreditNotesForm creditNote={creditNote as never} />;
}
